import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Download } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { Button, SegmentedControl, type Facet } from "@fox1ck-org/ui";
import { Conversion, ConversionStatus } from "@gen/tracker/v1/conversion_pb";

import { ListView } from "@/components/list-view";
import { CardFacets } from "@/components/campaign/facets";
import { usePatchFilters } from "@/components/campaign/use-patch-filters";
import { ConversionStatusBadge } from "@/components/canonical/conversion-status-badge";
import { ConversionDetailDrawer } from "@/components/canonical/conversion-detail-drawer";
import { ClickDetailDrawer } from "@/components/canonical/click-detail-drawer";
import { useTableParams } from "@/lib/hooks/use-table-params";
import { plural, useT } from "@/lib/i18n";
import { DASH, formatAmount, formatInt, formatMoney } from "@/lib/format";
import { formatStamp, parseDecimalString } from "@/lib/utils";
import { triggerCsvDownload } from "@/lib/csv";
import { statsRowToNumbers } from "@/lib/services/reporting-schema-service";
import { useCampaignReport } from "@/lib/services/campaign-report-service";
import { conversionRowsToCsv, conversionStatusText, fetchAllConversions, useSearchConversions } from "@/lib/services/conversion-service";
import { useLandersByIds } from "@/lib/services/lander-service";
import type { CardContext } from "./card";

const PAGE_SIZES = [50, 100, 250];

const STATUSES = {
  approved: ConversionStatus.APPROVED,
  hold: ConversionStatus.HOLD,
  rejected: ConversionStatus.REJECTED,
  trash: ConversionStatus.TRASH,
} as const;
type StatusKey = keyof typeof STATUSES | "all";

/** How long a lead still in hold has waited, in hours under a day. Pure. */
export function holdWait(c: Pick<Conversion, "status" | "leadTs" | "ts">, now = Date.now()): { hours: number; days: number } | undefined {
  if (c.status !== ConversionStatus.HOLD) return undefined;
  const at = Date.parse(c.leadTs || c.ts);
  if (!Number.isFinite(at)) return undefined;
  const h = Math.max(0, (now - at) / 3_600_000);
  return { hours: Math.max(1, Math.round(h)), days: Math.floor(h / 24) };
}

/** «120214…7731»: an ad id short enough for its column, both ends kept. Pure. */
export function shortAd(id: string): string {
  return id.length > 12 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

/** The money a row adds, as the board prints it: only an approve carries it. Pure. */
function counted(c: Conversion): boolean {
  return c.status === ConversionStatus.APPROVED;
}

/**
 * Конверсии кампании (доска CConversions): one row per lead — when it came,
 * its status and since when, the payout in the partner's currency and in USD,
 * the partner's lead id, the ad, the lander and the city of its click, and
 * the click itself. The switch counts every status under the other filters;
 * «Итого» is what the rows under every filter add to revenue (server). A row
 * opens the conversion; the click id opens the click.
 */
export function ConversionsTab({ ctx }: { ctx: CardContext }) {
  const t = useT();
  const params = useTableParams({ defaultPageSize: 50, pageSizeOptions: PAGE_SIZES });
  const patch = usePatchFilters();
  const statusKey = (params.getFilter("status") || "all") as StatusKey;
  const ad = params.getFilter("ad");
  const lander = params.getFilter("lander");
  const [selected, setSelected] = useState<Conversion | undefined>();
  const [clickId, setClickId] = useState<string | undefined>();
  const [exporting, setExporting] = useState(false);

  const filter = {
    range: { from: params.from, to: params.to, timezone: params.tz },
    campaignId: ctx.campaign.id,
    status: statusKey === "all" ? undefined : STATUSES[statusKey],
    creativeId: ad || undefined,
    landerId: lander || undefined,
    search: params.search || undefined,
  };
  const query = useSearchConversions({ ...filter, limit: params.pageSize, offset: params.pageIndex * params.pageSize });
  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const counts = query.data?.statusCounts;
  const all = counts ? [...counts.values()].reduce((a, b) => a + b, 0) : undefined;

  // The facets' options are the campaign's ads and landers with their leads
  // over the period — the report's numbers, so «0» is an answer.
  const base = { campaignId: ctx.campaign.id, from: params.from, to: params.to, tz: params.tz, basis: params.basis };
  const ads = useCampaignReport({ ...base, dims: ["creative"] });
  const landerRows = useCampaignReport({ ...base, dims: ["landing"] });
  const landerIds = useMemo(
    () => [...items.map((c) => c.landerId), ...(landerRows.data?.rows ?? []).map((r) => r.dimensionPath[0] ?? ""), lander].filter(Boolean),
    [items, landerRows.data, lander],
  );
  const landers = useLandersByIds(landerIds);
  const landerName = (id: string) => landers.data?.get(id)?.name ?? id.slice(0, 8);

  const facets: Facet[] = [
    {
      id: "ad",
      label: t("card.conv.ad"),
      kind: "options",
      searchable: true,
      loading: ads.isLoading,
      options: (ads.data?.rows ?? [])
        .filter((r) => r.dimensionPath[0])
        .map((r) => ({ value: r.dimensionPath[0]!, label: shortAd(r.dimensionPath[0]!), count: statsRowToNumbers(r.stats).leads ?? 0 })),
    },
    {
      id: "lander",
      label: t("card.clicks.lander"),
      kind: "options",
      searchable: true,
      loading: landerRows.isLoading,
      options: (landerRows.data?.rows ?? [])
        .filter((r) => r.dimensionPath[0])
        .map((r) => ({ value: r.dimensionPath[0]!, label: landerName(r.dimensionPath[0]!), count: statsRowToNumbers(r.stats).leads ?? 0 })),
    },
  ];

  const columns = useMemo<ColumnDef<Conversion>[]>(
    () => [
      {
        id: "lead",
        header: t("card.conv.leadAt"),
        size: 132,
        cell: ({ row }) => <span className="font-mono text-xs tabular-nums">{formatStamp(row.original.leadTs || row.original.ts, params.tz)}</span>,
      },
      {
        id: "status",
        header: t("cj.col.status"),
        size: 92,
        cell: ({ row }) => <ConversionStatusBadge status={row.original.status} />,
      },
      {
        id: "statusAt",
        header: t("card.conv.statusAt"),
        size: 104,
        cell: ({ row }) => {
          const w = holdWait(row.original);
          return (
            <span className="font-mono text-xs tabular-nums text-muted-foreground" title={w ? t("card.conv.waits", { n: w.days < 1 ? t("card.conv.wait.h", { n: w.hours }) : `${w.days} ${plural(t("word.days"), w.days)}` }) : undefined}>
              {formatStamp(row.original.ts, params.tz)}
            </span>
          );
        },
      },
      {
        id: "payout",
        header: t("card.conv.payout"),
        size: 92,
        meta: { align: "right" },
        cell: ({ row }) => {
          const c = row.original;
          if (!counted(c) || !c.payout?.amount) return <span className="text-muted-foreground">{DASH}</span>;
          return <span className="font-mono tabular-nums">{formatAmount(parseDecimalString(c.payout.amount), c.payout.currency || "USD")}</span>;
        },
      },
      {
        id: "usd",
        header: t("card.conv.usd"),
        size: 72,
        meta: { align: "right" },
        cell: ({ row }) => {
          const c = row.original;
          if (!counted(c)) return <span className="text-muted-foreground">{DASH}</span>;
          if (!c.reportingPayoutUsd) return c.payout?.amount ? <span className="text-xs text-warn">{t("cj.noRate")}</span> : <span className="text-muted-foreground">{DASH}</span>;
          return <span className="font-mono tabular-nums">{formatMoney(parseDecimalString(c.reportingPayoutUsd), { unit: true })}</span>;
        },
      },
      {
        id: "txid",
        header: t("card.conv.txidNet"),
        size: 104,
        cell: ({ row }) => <span className="block truncate font-mono text-xs">{row.original.txid || DASH}</span>,
      },
      {
        id: "ad",
        header: t("card.conv.ad"),
        size: 120,
        cell: ({ row }) => (
          <span className="block truncate font-mono text-xs" title={row.original.creativeId}>
            {row.original.creativeId ? shortAd(row.original.creativeId) : DASH}
          </span>
        ),
      },
      {
        id: "lander",
        header: t("card.clicks.lander"),
        cell: ({ row }) => <span className="block truncate">{row.original.landerId ? landerName(row.original.landerId) : DASH}</span>,
      },
      {
        id: "city",
        header: t("card.conv.city"),
        size: 110,
        cell: ({ row }) => <span className="block truncate">{row.original.city || row.original.country || DASH}</span>,
      },
      {
        id: "click",
        header: t("card.conv.click"),
        size: 96,
        cell: ({ row }) =>
          row.original.clickId ? (
            <button
              type="button"
              className="truncate font-mono text-xs text-primary hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                setClickId(row.original.clickId);
              }}
            >
              {row.original.clickId.slice(0, 6)}…
            </button>
          ) : (
            DASH
          ),
      },
    ],
    // landerName reads landers.data; the columns follow it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, params.tz, landers.data],
  );

  const statusOptions = (["all", "approved", "hold", "rejected", "trash"] as const).map((k) => ({
    value: k,
    label: k === "all" ? t("cj.status.all") : conversionStatusText(STATUSES[k]),
    count: k === "all" ? all : (counts?.get(STATUSES[k]) ?? (counts ? 0 : undefined)),
  }));
  const total = query.data?.total ?? 0;
  const revenue = query.data?.revenueUsd ? parseDecimalString(query.data.revenueUsd) : undefined;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows = await fetchAllConversions(filter);
      triggerCsvDownload(conversionRowsToCsv(rows), `${ctx.campaign.name}-conversions-${params.from}-${params.to}.csv`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("campaign.toast.failed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <SegmentedControl
          size="sm"
          aria-label={t("cj.col.status")}
          value={statusKey}
          onValueChange={(v: string) => patch({ status: v === "all" ? null : v })}
          options={statusOptions}
        />
        <CardFacets facets={facets} value={{ ad, lander }} onChange={patch} />
        <div className="flex-1" />
        <span className="max-w-80 text-xs text-muted-foreground">{t("card.conv.caption", { tz: params.tz })}</span>
        <Button variant="secondary" size="sm" onClick={() => void exportCsv()} disabled={exporting || total === 0}>
          <Download className="size-3.5" /> CSV
        </Button>
      </div>
      <ListView
        failure={query.error ? { title: t("cj.error"), error: query.error, retry: () => void query.refetch() } : undefined}
        columns={columns}
        data={items}
        getRowId={(c) => c.dedupKey}
        loading={query.isLoading}
        manualSorting
        density="compact"
        rowClassName={(c) => (selected?.dedupKey === c.dedupKey ? "bg-primary-soft" : undefined)}
        totals={
          items.length > 0
            ? {
                lead: <span className="whitespace-nowrap font-semibold">{t("card.conv.total", { n: formatInt(total), leads: plural(t("word.leads"), total) })}</span>,
                usd: <span className="font-mono font-semibold tabular-nums">{formatMoney(revenue)}</span>,
              }
            : undefined
        }
        pagination={{
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          totalCount: total,
          onPageIndexChange: params.setPageIndex,
          onPageSizeChange: params.setPageSize,
          pageSizeOptions: PAGE_SIZES,
        }}
        onRowClick={setSelected}
        emptyMessage={t("card.conv.empty")}
      />
      <ConversionDetailDrawer
        conversion={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(undefined)}
        onOpenClick={(id) => {
          setSelected(undefined);
          setClickId(id);
        }}
      />
      <ClickDetailDrawer
        clickId={clickId}
        open={!!clickId}
        onOpenChange={(o) => !o && setClickId(undefined)}
        onOpenConversion={(c) => {
          setClickId(undefined);
          setSelected(c);
        }}
      />
    </div>
  );
}
